import { useMemo, useEffect } from 'react';
import { initializePerformanceOptimizations, optimizeAnimations, prefersReducedMotion } from '../utils/performanceOptimizer';

/**
 * @typedef {Object} PageTemplateConfig
 * @property {string} title - Page title
 * @property {string} [description] - Page description
 * @property {string} [icon] - FontAwesome class name (e.g., 'fas fa-home')
 * @property {string} [iconImage] - Image path for legacy icon support
 * @property {'default'|'narrow'|'wide'|'full'} [maxWidth='default'] - Container max width
 * @property {'default'|'none'|'large'} [padding='default'] - Container padding
 * @property {Array<PageAction>} [actions] - Header action buttons
 * @property {boolean} [enablePerformanceOptimizations=true] - Enable performance optimizations
 */

/**
 * @typedef {Object} PageAction
 * @property {string} label - Button label
 * @property {string} [icon] - FontAwesome icon class
 * @property {'primary'|'secondary'|'success'} variant - Button style variant
 * @property {Function} onClick - Click handler
 * @property {boolean} [disabled=false] - Whether button is disabled
 */

/**
 * Custom hook for standardized page template functionality with performance optimizations
 * @param {PageTemplateConfig} config - Page configuration
 * @returns {Object} Template utilities and CSS classes
 */
export const usePageTemplate = (config) => {
  const {
    title,
    description,
    icon,
    iconImage,
    maxWidth = 'default',
    padding = 'default',
    actions = [],
    enablePerformanceOptimizations = true
  } = config;

  // Initialize performance optimizations
  useEffect(() => {
    if (enablePerformanceOptimizations) {
      // Check if this is a gallery page
      const isGalleryPage = title.toLowerCase().includes('gallery') || 
                           title.toLowerCase().includes('photos') ||
                           document.querySelector('.unified-gallery-container');
      
      initializePerformanceOptimizations({
        removeUnused: true,
        measureMetrics: process.env.NODE_ENV === 'development',
        optimizeImagesOnLoad: true,
        disableForGalleries: isGalleryPage // Be gentler on gallery pages
      });
    }
  }, [enablePerformanceOptimizations, title]);

  // Generate standardized CSS classes with performance considerations
  const containerClasses = useMemo(() => {
    const classes = ['page-container-wrapper'];
    classes.push(`max-width-${maxWidth}`);
    classes.push(`padding-${padding}`);
    
    // Add performance-related classes
    if (enablePerformanceOptimizations) {
      classes.push('optimized');
    }
    
    return classes.join(' ');
  }, [maxWidth, padding, enablePerformanceOptimizations]);

  // Enhanced icon mapping for FontAwesome migration
  const iconMapping = useMemo(() => ({
    // Legacy image icons to FontAwesome mappings
    '/images/hello.png': 'fas fa-home',
    '/images/apple.png': 'fas fa-apple-alt',
    '/images/futbol.png': 'fas fa-futbol',
    '/images/rossetti.png': 'fas fa-tree',
    '/images/pane.png': 'fas fa-bread-slice',
    '/images/wine.png': 'fas fa-wine-glass-alt',
    '/images/jeep.png': 'fas fa-car',
    '/images/calendar.png': 'fas fa-calendar-alt',
    // Gallery-specific icons
    'gallery-default': 'fas fa-images',
    'gallery-wedding': 'fas fa-heart',
    'gallery-travel': 'fas fa-plane',
    'gallery-food': 'fas fa-utensils',
    // Add more mappings as needed
  }), []);

  // Resolve icon with performance considerations
  const resolvedIcon = useMemo(() => {
    if (icon) {
      return { type: 'fontawesome', value: icon };
    }
    if (iconImage) {
      const mappedIcon = iconMapping[iconImage];
      if (mappedIcon) {
        return { type: 'fontawesome', value: mappedIcon };
      }
      return { type: 'image', value: iconImage };
    }
    return null;
  }, [icon, iconImage, iconMapping]);

  // Generate page header props with optimizations
  const headerProps = useMemo(() => ({
    title,
    description,
    icon: resolvedIcon,
    actions: actions.map(action => ({
      ...action,
      className: `page-action-btn ${action.variant}`,
      // Add performance optimizations for button interactions
      onClick: enablePerformanceOptimizations ? 
        (e) => {
          // Optimize animations if user prefers reduced motion
          if (prefersReducedMotion()) {
            e.target.style.transition = 'none';
          }
          action.onClick(e);
        } : action.onClick
    }))
  }), [title, description, resolvedIcon, actions, enablePerformanceOptimizations]);

  // Utility functions with performance enhancements
  const getStandardizedClasses = (element) => {
    const classMap = {
      container: containerClasses,
      header: 'page-header',
      content: 'page-content',
      section: 'section',
      title: 'page-header h1',
      description: 'page-header p',
      actions: 'page-header-actions'
    };
    return classMap[element] || '';
  };

  // Performance-aware icon element generator
  const getIconElement = () => {
    if (!resolvedIcon) return null;
    
    if (resolvedIcon.type === 'fontawesome') {
      return { 
        tag: 'i', 
        className: resolvedIcon.value,
        // Add performance attributes
        'aria-hidden': 'true',
        'data-optimized': enablePerformanceOptimizations
      };
    }
    
    return { 
      tag: 'img', 
      src: resolvedIcon.value, 
      className: 'page-header-icon',
      // Add performance attributes for images
      loading: 'eager', // Header icons should load immediately
      decoding: 'sync',
      'data-optimized': enablePerformanceOptimizations
    };
  };

  return {
    containerClasses,
    headerProps,
    resolvedIcon,
    getStandardizedClasses,
    getIconElement,
    // Helper methods
    isUsingFontAwesome: () => resolvedIcon?.type === 'fontawesome',
    isOptimized: () => enablePerformanceOptimizations,
    // Performance utilities
    optimizeElement: (element) => {
      if (enablePerformanceOptimizations && element) {
        optimizeAnimations(element);
      }
    }
  };
};

/**
 * Utility function to create standardized gallery configuration with performance optimizations
 * @param {string} galleryType - Type of gallery (e.g., 'futbol', 'rossetti')
 * @param {Object} [customConfig] - Custom configuration overrides
 * @returns {Object} Standardized gallery configuration
 */
export const createStandardGalleryConfig = (galleryType, customConfig = {}) => {
  const defaultConfig = {
    features: {
      slideshow: true,
      thumbnails: true,
      fullscreen: true,
      zoom: true,
      captions: true,
      sharing: false,
      // Performance features
      lazyLoading: true,
      imageOptimization: true,
      preloadAdjacent: 2 // Preload 2 images on each side
    },
    layout: {
      thumbnailSize: 220,
      maxDisplay: null,
      columns: 'auto',
      spacing: 'default'
    },
    performance: {
      enableIntersectionObserver: true,
      rootMargin: 50,
      enableWebP: true,
      enableProgressiveLoading: true
    }
  };

  return {
    type: galleryType,
    ...defaultConfig,
    ...customConfig,
    features: { ...defaultConfig.features, ...customConfig.features },
    layout: { ...defaultConfig.layout, ...customConfig.layout },
    performance: { ...defaultConfig.performance, ...customConfig.performance }
  };
};

export default usePageTemplate;